/**
 * Demo API utilities
 * Demo endpoints don't require authentication
 */

const API_BASE_URL = `${process.env.REACT_APP_DOCS_API_KEY}`;

/**
 * Generate OCR-compliant JSON from PDF files (Demo)
 * @param {File[]} files - Array of PDF files to process
 * @param {string} email - User email address (required)
 * @param {Array} specifyAmount - JSON array of specific amounts/partidas to extract (optional)
 * @param {string} userContext - Additional user context for processing (optional)
 * @param {string} company - Company name (optional string; sent as form field `company`)
 * @returns {Promise<Response>} - Response from the API
 */
export const demoOcrGenerate = async (
  files,
  email,
  specifyAmount = [],
  userContext = '',
  company = ''
) => {
  if (!files || files.length === 0) {
    throw new Error('No PDF files provided');
  }

  if (!email || typeof email !== 'string' || email.trim() === '') {
    throw new Error('Email address is required');
  }

  // Filter to only actual File objects
  const validFiles = Array.isArray(files) 
    ? files.filter(file => file instanceof File)
    : [];

  if (validFiles.length === 0) {
    throw new Error('No valid PDF files found');
  }

  const formData = new FormData();
  
  // Append files
  validFiles.forEach(file => {
    formData.append('files', file);
  });

  // Append email (required)
  formData.append('email', email.trim());

  // Append company (string; backend may require this alongside email)
  formData.append('company', company != null ? String(company).trim() : '');

  // Append specifyAmount if provided
  if (specifyAmount && Array.isArray(specifyAmount) && specifyAmount.length > 0) {
    formData.append('specifyAmount', JSON.stringify(specifyAmount));
  }

  // Append user_context if provided
  if (userContext && typeof userContext === 'string' && userContext.trim().length > 0) {
    formData.append('user_context', userContext.trim());
  }

  // Validate API_BASE_URL is set
  if (!API_BASE_URL || API_BASE_URL === 'undefined') {
    throw new Error('API base URL is not configured. Please check REACT_APP_DOCS_API_KEY environment variable.');
  }

  const url = `https://${API_BASE_URL}/api/demo/`;
  
  console.log('Demo API Request:', {
    url,
    filesCount: validFiles.length,
    email: email.trim(),
    company: company != null ? String(company).trim() : '',
    hasSpecifyAmount: specifyAmount && specifyAmount.length > 0,
    hasUserContext: userContext && userContext.trim().length > 0
  });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'accept': 'application/json'
        // Don't set Content-Type - let browser set it with boundary for FormData
      },
      body: formData
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      let rateLimit = null;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorData.detail || errorMessage;
        
        // Check if this is a rate limit error - only when API explicitly returns error response
        // Must have status "error" and message containing "rate limit exceeded"
        const isErrorStatus = errorData.status === 'error' || response.status >= 400;
        const isRateLimitMessage = errorMessage.toLowerCase().includes('rate limit exceeded') || 
                                   errorMessage.toLowerCase().includes('rate_limit') ||
                                   response.status === 429;
        
        if (isErrorStatus && isRateLimitMessage) {
          // When rate limit is exceeded, set remaining to 0
          const apiRateLimit = errorData.rateLimit || {
            remaining: errorData.remaining !== undefined ? errorData.remaining : 0,
            maxRequests: errorData.maxRequests || 5
          };
          
          // Extract timestamp from error message (format: "Please try again after 2026-01-18T12:27:17.392Z")
          let resetTime = null;
          // More specific pattern to match ISO 8601 timestamp
          const timestampPattern = /after\s+(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z)/i;
          const timestampMatch = errorMessage.match(timestampPattern);
          if (timestampMatch && timestampMatch[1]) {
            resetTime = timestampMatch[1];
          }
          
          // If rate limit is exceeded (error response), ensure remaining is 0
          rateLimit = {
            remaining: 0, // Rate limit exceeded means 0 remaining
            maxRequests: apiRateLimit.maxRequests || 5,
            resetTime: resetTime // Include reset timestamp
          };
        }
      } catch (e) {
        // If response is not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }
      
      const error = new Error(errorMessage);
      if (rateLimit) {
        error.rateLimit = rateLimit;
        error.isRateLimitError = true;
      }
      throw error;
    }

    return response;
  } catch (error) {
    // Enhanced error logging
    if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      console.error('Demo API Fetch Error - Possible causes:', {
        url,
        error: error.message,
        possibleCauses: [
          'CORS issue - server may not allow requests from this origin',
          'Network error - server may be unreachable',
          'Endpoint does not exist',
          'SSL/TLS certificate issue'
        ]
      });
      throw new Error('Failed to connect to demo API. Please check your network connection and ensure the demo endpoint is available.');
    }
    console.error('Error calling demo OCR generate API:', error);
    throw error;
  }
};

/**
 * Get demo job status
 * @param {string} jobId - Job ID to check status for
 * @returns {Promise<Response>} - Response from the API
 */
export const getDemoJobStatus = async (jobId) => {
  if (!jobId || typeof jobId !== 'string') {
    throw new Error('Job ID is required');
  }

  const url = `https://${API_BASE_URL}/api/demo/queue/${jobId}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error('Error getting demo job status:', error);
    throw error;
  }
};

/**
 * Stream demo job updates via Server-Sent Events
 * @param {string} jobId - Job ID to stream updates for
 * @param {Function} onMessage - Callback function for each message received
 * @param {Function} onError - Callback function for errors
 * @returns {EventSource} - EventSource instance for the stream
 */
export const streamDemoJobUpdates = (jobId, onMessage, onError) => {
  if (!jobId || typeof jobId !== 'string') {
    throw new Error('Job ID is required');
  }

  const url = `https://${API_BASE_URL}/api/demo/queue/${jobId}/stream`;

  const eventSource = new EventSource(url);

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (onMessage) {
        onMessage(data);
      }
    } catch (error) {
      console.error('Error parsing SSE message:', error);
      if (onError) {
        onError(error);
      }
    }
  };

  eventSource.onerror = (error) => {
    console.error('EventSource error:', error);
    if (onError) {
      onError(error);
    }
  };

  return eventSource;
};

/**
 * Get cooldown status for an email (Admin only)
 * @param {string} email - Email address to check cooldown for
 * @returns {Promise<Response>} - Response from the API
 */
export const getDemoCooldownStatus = async (email) => {
  if (!email || typeof email !== 'string') {
    throw new Error('Email address is required');
  }

  const url = `https://${API_BASE_URL}/api/demo/cooldown/${encodeURIComponent(email)}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error('Error getting demo cooldown status:', error);
    throw error;
  }
};

/**
 * Remove email from demo cooldown (Admin only)
 * @param {string} email - Email address to remove from cooldown
 * @returns {Promise<Response>} - Response from the API
 */
export const removeDemoCooldown = async (email) => {
  if (!email || typeof email !== 'string') {
    throw new Error('Email address is required');
  }

  const url = `https://${API_BASE_URL}/api/demo/cooldown/${encodeURIComponent(email)}`;

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error('Error removing demo cooldown:', error);
    throw error;
  }
};

